import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { CommonsFacade } from '@state/commons';

import { ConsumableCountry } from '@interfaces';
import { AvailableLanguages } from '@constants';
import { debounceTime, take, takeUntil } from 'rxjs/operators';
import { merge, Observable, ReplaySubject, Subscription } from 'rxjs';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  customDateComparisonValidator,
  customDateLengthValidator,
  forbiddenFutureDate,
  forbiddenDateInPast,
  DateFormat,
} from '@shared/custom-validators';
import { ManagementFacade } from '@state/management';
import { UserProcessFacade } from '@state/user-process';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { getParsedVisitsWithSortDate, isSameVisitOrder } from '@shared/personal-data-form/visits-sort.helper';

@Component({
  selector: 'app-countries-visits-form-long-resi',
  templateUrl: './countries-visits-form-long-resi.component.html',
  styleUrls: ['./countries-visits-form-long-resi.component.scss'],
  standalone: false,
})
export class CountriesVisitsFormLongResiComponent implements OnInit, OnChanges, OnDestroy {
  private readonly commonsFacade = inject(CommonsFacade);
  private readonly translateService = inject(TranslateService);
  private readonly fb = inject(FormBuilder);
  private readonly userProcessFacade = inject(UserProcessFacade);
  private readonly managementFacade = inject(ManagementFacade);
  private readonly notifService = inject(StackedMessagesService);

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() lastTravelsOutsidePoland: AbstractControl;
  @Input() triggerSort: Observable<void>;
  @Input() disabled = false;

  @Output() sortingChanged = new EventEmitter<boolean>();
  @Output() loadingChanged = new EventEmitter<boolean>();

  public countriesList: ConsumableCountry[];
  public currentLang: AvailableLanguages;
  public visitsArray: FormGroup[] = [];

  public isSorting = false;
  public isLoading = false;
  public isSaved = false;

  public savedSuccess$ = merge(
    this.userProcessFacade.updatePersonalDetailsSuccess$,
    this.managementFacade.updatePersonalDetailsSuccess$
  );

  public oldTravelsValue: string;

  private groupsValueChangesSubscriptions: Subscription[] = [];

  public ngOnInit(): void {
    this.currentLang = this.translateService.currentLang as AvailableLanguages;

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(({ lang: langKey }) => {
      this.currentLang = langKey as AvailableLanguages;
    });

    this.triggerSort.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.sortCountriesVisits();
    });

    this.commonsFacade
      .getCountries$()
      .pipe(take(1))
      .subscribe(countriesList => {
        this.countriesList = countriesList;
        const visitsString = this.lastTravelsOutsidePoland.value as string | null;
        this.oldTravelsValue = visitsString;
        // if (!visitsString?.length) { return; }

        // visitsString is in format:
        // `[countryName]: [dateEnter]-[dateLeave], [countryName]: [dateEnter]-[dateLeave], [countryName]: [dateEnter]-[dateLeave]`
        // so we can use comma as delimiter for visit
        const visits = visitsString?.split(',');
        if (!visits?.length) {
          const { group, groupSubscription } = this.getNewVisitGroup();
          this.visitsArray.push(group);
          this.groupsValueChangesSubscriptions.push(groupSubscription);
          this.applyDisabledState();
          return;
        }

        visits.forEach(visitString => {
          const { group, groupSubscription } = this.getNewVisitGroup(visitString.trim());
          this.visitsArray.push(group);
          this.groupsValueChangesSubscriptions.push(groupSubscription);
        });

        this.saveVisits();
        this.applyDisabledState();
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled'] && this.visitsArray.length) {
      this.applyDisabledState();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private applyDisabledState(): void {
    this.visitsArray.forEach(group => {
      if (this.disabled && group.enabled) {
        group.disable({ emitEvent: false });
      } else if (!this.disabled && group.disabled) {
        group.enable({ emitEvent: false });
      }
    });
  }

  public getControl(name: string, groupIndex: number): FormControl {
    return this.visitsArray[groupIndex].get(name) as FormControl;
  }

  public addNewVisit(): void {
    this.isSaved = false;
    const { group, groupSubscription } = this.getNewVisitGroup();
    this.visitsArray.push(group);
    this.groupsValueChangesSubscriptions.push(groupSubscription);
    this.applyDisabledState();
  }

  public getNewVisitGroup(visitString?: string): { group: FormGroup; groupSubscription: Subscription } {
    const visitDateValidatorsArray = [
      Validators.required,
      customDateLengthValidator({ format: DateFormat['DD.MM.YYYY'] }),
      forbiddenDateInPast({ specificDate: '01.01.2015' }),
      forbiddenFutureDate(),
    ];

    const group: FormGroup<any> = this.fb.group(
      {
        country: [null, Validators.compose([Validators.required])],
        dateEnter: [null, Validators.compose([...visitDateValidatorsArray])],
        dateLeave: [null, Validators.compose([...visitDateValidatorsArray])],
      },
      { validators: customDateComparisonValidator('dateEnter', 'dateLeave') }
    );

    // visitString is in form: `[countryName]: [dateEnter]-[dateLeave]`
    if (visitString) {
      const [country, dates] = visitString.split(':');
      if (country?.length && dates?.length) {
        const [dateEnter, dateLeave] = dates.trim().split('-');
        group.patchValue({ country, dateEnter, dateLeave });
      }
    }

    const groupSubscription = group.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(500)).subscribe(() => {
      this.isSaved = false;
      if (!group.valid) {
        return;
      }
      this.saveVisits();
    });
    return { group, groupSubscription };
  }

  public removeVisit(groupIndex: number): void {
    // Not possible to remove last visit
    if (this.visitsArray.length === 1) {
      return;
    }
    this.isSaved = false;
    this.visitsArray.splice(groupIndex, 1);
    const sub = this.groupsValueChangesSubscriptions[groupIndex];
    sub.unsubscribe();
    this.groupsValueChangesSubscriptions.splice(groupIndex, 1);
    this.saveVisits();
  }

  public saveVisits(): void {
    const allCorrect = this.visitsArray.every(fg => fg.valid);
    if (!allCorrect) {
      return;
    }

    this.setIsLoading(true);
    this.isSaved = false;
    const visits: string[] = this.visitsArray.map(visitGroup => {
      const { country, dateEnter, dateLeave } = visitGroup.value;
      return `${country.trim()}: ${dateEnter.trim()}-${dateLeave.trim()}`;
    });

    const visitsString = visits.join(', ').trim();

    if (this.oldTravelsValue !== visitsString) {
      this.savedSuccess$.pipe(take(1)).subscribe(() => {
        this.setIsLoading(false);
        this.isSaved = true;
      });
    } else {
      this.setIsLoading(false);
      this.isSaved = true;
    }

    // If empty string then user did not provide any info - null;
    const valToSet = visitsString ? visitsString : null;
    this.lastTravelsOutsidePoland.setValue(valToSet);
    this.oldTravelsValue = valToSet;
  }

  public sortCountriesVisits(): void {
    if (!this.visitsArray.length) {
      return;
    }

    const parsed = getParsedVisitsWithSortDate(this.visitsArray);
    if (isSameVisitOrder(parsed)) {
      this.notifService.showInfo({ message: 'COUNTRIES_VISITS_FORM.SORT_VISITS_SUCCESSFULLY' });
      return;
    }

    this.isSaved = false;
    this.setIsLoading(true);
    this.setIsSorting(true);

    setTimeout(() => {
      this.groupsValueChangesSubscriptions.forEach(sub => sub.unsubscribe());
      this.groupsValueChangesSubscriptions = [];

      this.visitsArray = parsed.map(p => p.group);
      this.visitsArray.forEach(group => {
        const sub = group.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(500)).subscribe(() => {
          this.isSaved = false;
          if (!group.valid) {
            return;
          }
          this.saveVisits();
        });

        this.groupsValueChangesSubscriptions.push(sub);
      });

      this.saveVisits();

      this.isSaved = true;
      this.setIsLoading(false);
      this.setIsSorting(false);

      this.notifService.showInfo({ message: 'COUNTRIES_VISITS_FORM.SORT_VISITS_SUCCESSFULLY' });
    }, 500);
  }

  public setIsSorting(value: boolean): void {
    this.isSorting = value;
    this.sortingChanged.emit(value);
  }

  public setIsLoading(value: boolean): void {
    this.isLoading = value;
    this.loadingChanged.emit(value);
  }
}
